"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import SocBadge from "@/components/brand/SocBadge";
import { trackEvent } from "@/lib/analytics";
import { EVENT } from "@/lib/analytics-events";
import { TRUST_PORTAL_LABEL, TRUST_PORTAL_URL } from "@/lib/contact";

const footerLinks = {
  product: [
    { title: "Platform", href: "/platform", link_id: "footer_platform" },
    { title: "Product", href: "/product", link_id: "footer_product" },
    { title: "Solutions", href: "/solutions", link_id: "footer_solutions" },
    {
      title: "Security & Compliance",
      href: "/security",
      link_id: "footer_security",
    },
  ],
  company: [
    { title: "About", href: "/about", link_id: "footer_about" },
    { title: "Newsroom", href: "/newsroom", link_id: "footer_newsroom" },
    { title: "Team", href: "/team", link_id: "footer_team" },
    { title: "Contact", href: "/contact", link_id: "footer_contact" },
  ],
  resources: [
    { title: "Support", href: "/support", link_id: "footer_support" },
    { title: "Join waitlist", href: "/waitlist", link_id: "footer_waitlist" },
  ],
  legal: [
    { title: "Privacy Policy", href: "/privacy", link_id: "footer_privacy" },
    {
      title: "Terms of Service",
      href: "/terms",
      link_id: "footer_terms",
    },
  ],
} as const;

const socialLinks = [
  {
    href: "https://www.linkedin.com/company/zomtech/posts/?feedView=all",
    label: "LinkedIn",
    link_id: "footer_linkedin",
    icon: (
      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
] as const;

const financialDisclaimer = {
  label: "Important Disclaimer",
  body: "ZOM AI provides financial data and analysis tools for informational purposes only. Nothing on this platform constitutes investment advice; all investment decisions should be made after consulting a qualified professional. Past performance does not guarantee future results. Trading involves risk and may result in loss.",
} as const;

/**
 * The Lumina landing is pinned to a literal white surface and never theme-flips,
 * even when the OS/next-themes resolves to dark (`html.dark` is still present).
 * So the footer must NOT use theme-flipping semantic slots (--background,
 * --foreground, --card, --border, --fg2) — those resolve to dark under html.dark
 * and produce a dark footer on the light page. Everything below is built from
 * STATIC brand tokens (--zom-* / --ld-*) and color-mixes of them, so the footer
 * stays neutral-light in every theme. No hardcoded color literals.
 *
 * Local tints are color-mixes of static tokens:
 *   --ft-surface (footer fill)  --ft-line (hairline)  --ft-body (body copy)
 */
export default function Footer() {
  const handleInternalNav = (link_id: string, href: string) => () => {
    trackEvent(EVENT.marketing_footer_clicked, {
      link_id,
      destination: href,
    });
  };

  return (
    <footer
      data-brand-key="footer"
      className="mb-0 border-t border-[color:var(--ft-line)] bg-[var(--ft-surface)] py-[var(--zom-section-y-compact)] text-[var(--zom-graphite)] [--ft-body:color-mix(in_srgb,var(--zom-graphite),var(--zom-gunmetal))] [--ft-line:color-mix(in_srgb,var(--zom-graphite)_12%,var(--ld-surface-white))] [--ft-surface:color-mix(in_srgb,var(--zom-graphite)_3%,var(--ld-surface-white))]"
    >
      <div className="mx-auto max-w-[var(--zom-page-max-wide)] px-[var(--marketing-gutter)]">
        <div className="mb-12 grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-6">
          {/* Brand Column - Takes 2 columns on large screens */}
          <div className="sm:col-span-2 lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Link
                href="/"
                className="mb-6 flex items-center gap-3"
                onClick={handleInternalNav("footer_logo", "/")}
              >
                <Image
                  src="/zom-logo-dark.png"
                  alt="Zom"
                  width={32}
                  height={32}
                  className="h-8 w-auto"
                />
                <span className="text-xl font-medium text-[var(--zom-graphite)]">
                  Zom
                </span>
              </Link>

              <p className="mb-6 max-w-sm text-sm leading-relaxed text-[color:var(--ft-body)]">
                Institutional-grade financial intelligence for boutique RIAs.
                Transform the way you analyze markets and serve clients.
              </p>

              {/* Trust Center Card */}
              <div className="mb-6 rounded-[var(--zom-panel-radius)] border border-[color:var(--ft-line)] bg-[var(--ld-surface-white)] p-4 shadow-sm">
                <p className="mb-2 text-[10px] font-medium text-[var(--zom-gunmetal)] uppercase">
                  Zomtech Trust Center
                </p>
                <p className="mb-2 text-xs leading-relaxed text-[color:var(--ft-body)]">
                  Zom (zom.ai) is the AI financial advisory assistant. Make
                  data-driven decisions twice as good, and ten times as fast.
                </p>
                <a
                  href={TRUST_PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackEvent(EVENT.marketing_footer_clicked, {
                      link_id: "footer_trust_center",
                      destination: TRUST_PORTAL_URL,
                    })
                  }
                  className="text-xs font-medium text-[var(--zom-graphite)] underline-offset-4 hover:underline"
                >
                  {TRUST_PORTAL_LABEL}
                </a>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-3">
                {socialLinks.map(({ href, label, icon, link_id }) => (
                  <motion.a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackEvent(EVENT.marketing_footer_clicked, {
                        link_id,
                        destination: href,
                      })
                    }
                    whileHover={{ y: -2, scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="group flex h-9 w-9 items-center justify-center rounded-lg border border-[color:var(--ft-line)] bg-[var(--ld-surface-white)] text-[var(--zom-gunmetal)] transition-all hover:border-[var(--zom-graphite)] hover:bg-[var(--zom-graphite)] hover:text-[var(--ld-surface-white)]"
                    aria-label={label}
                  >
                    {icon}
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Link Columns */}
          {Object.entries(footerLinks).map(([category, links], index) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 + index * 0.05 }}
            >
              <h4 className="mb-4 text-[11px] font-medium text-[var(--zom-graphite)] uppercase">
                {category}
              </h4>
              <ul className="space-y-2.5">
                {links.map(({ title, href, link_id }) => (
                  <li key={title}>
                    <Link
                      href={href}
                      className="group inline-block text-sm text-[var(--zom-gunmetal)] transition-colors hover:text-[var(--zom-graphite)]"
                      onClick={handleInternalNav(link_id, href)}
                    >
                      {title}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="border-t border-[color:var(--ft-line)] pt-8"
        >
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <p className="text-xs text-[var(--zom-gunmetal)]">
              © {new Date().getFullYear()} Zom AI, Inc. All rights reserved.
            </p>

            {/* Status Badges */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 rounded-lg border border-[color:var(--ft-line)] bg-[var(--ld-surface-white)] px-3 py-1.5">
                <div className="h-1.5 w-1.5 rounded-full bg-[var(--zom-gain)]" />
                <span className="text-[10px] font-medium text-[color:var(--ft-body)] uppercase">
                  99.9% Uptime
                </span>
              </div>
              <a
                href={TRUST_PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackEvent(EVENT.marketing_footer_clicked, {
                    link_id: "footer_soc2_pill",
                    destination: TRUST_PORTAL_URL,
                  })
                }
                className="flex items-center gap-2 rounded-lg border border-[color:var(--ft-line)] bg-[var(--ld-surface-white)] px-3 py-1.5 transition-all hover:border-[var(--zom-graphite)]"
              >
                <div className="h-1.5 w-1.5 rounded-full bg-[color:var(--zom-cobalt-400)]" />
                <span className="text-[10px] font-medium text-[color:var(--ft-body)] uppercase">
                  SOC 2 Type II
                </span>
              </a>
              <SocBadge
                variant="color"
                size={40}
                className="h-10 w-10"
                linkClassName="inline-flex rounded-full transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--zom-graphite)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ft-surface)]"
                trackingLinkId="footer_soc2_badge"
              />
            </div>
          </div>

          {/* Disclaimer */}
          <div
            className="relative mt-6 rounded-[var(--zom-panel-radius)] border border-[color:var(--ft-line)] bg-[var(--ld-surface-white)] p-5"
            role="region"
            aria-label="Important disclosures"
          >
            <p className="text-[11px] leading-relaxed text-[color:var(--ft-body)]">
              <strong className="font-medium text-[var(--zom-graphite)]">
                {financialDisclaimer.label}:
              </strong>
              &nbsp;{financialDisclaimer.body}
            </p>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
